// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import { useEvolvePaginatedGet, type EvolveApiParams } from '@evolvemep/api-ts';
import { Stack, Text, LoadingOverlay } from '@mantine/core';

import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { MobileBasePageHeader } from 'components/Mantine/MobileBasePageHeader';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { SelectableWorkOrderCard } from 'modules/Shop/WorkOrders/WorkOrdersPage/Mobile/SelectableWorkOrderCard';
import { NewWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';
import type { WorkOrdersPageProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrdersPage';

type WorkOrderSectionProps = {
  title: string;
  actionable: boolean;
  defaultParams: EvolveApiParams<'GET_PAGINATED', 'shop/workOrder'>;
};

const WorkOrderSection = ({ title, actionable, defaultParams }: WorkOrderSectionProps) => {
  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: { actionable, ...defaultParams },
      },
    }),
    [actionable, defaultParams],
  );
  const {
    data: workOrders,
    entireCount,
    loading,
    fetchNextPage,
    setDefaultOpts,
  } = useEvolvePaginatedGet('shop/workOrder', defaultOpts);
  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  return (
    <Stack gap="xs">
      <Text c="dimmed" fw={600}>
        {title} {isNotNil(entireCount) && `(${entireCount})`}
      </Text>
      {workOrders.map((workOrder) => (
        <SelectableWorkOrderCard key={workOrder.workOrderId} workOrder={workOrder} />
      ))}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
    </Stack>
  );
};

const MobileWorkOrdersPageWrapped = ({ states }: WorkOrdersPageProps) => {
  const activeStates = useMemo(() => states.filter((s) => s.name !== 'Completed'), [states]);
  const { facilityIds, projectIds, assigneeIds, activeAll, projects } = useGlobalFilters({
    projects: true,
    facilities: true,
    assignees: 'shop/workOrder/assignedUsers',
    activeAll: {
      key: 'Work Orders',
      defaultState: 'active',
      whenActive: activeStates,
      allPossible: states,
    },
  });

  const defaultParams = useMemo<EvolveApiParams<'GET_PAGINATED', 'shop/workOrder'>>(
    () => ({
      facilityIds,
      projectIds,
      assigneeIds,
      workOrderStateIds: activeAll.value?.map((s) => s.stateId),
    }),
    [facilityIds, projectIds, assigneeIds, activeAll],
  );

  return (
    <Stack flex={1} mih={0} w="100%" p="md" pb={0} gap="sm">
      <MobileBasePageHeader
        title="Work Orders"
        rightSideComponent={
          <NewWorkOrderModal defaultProject={hasLengthExactly(projects, 1) ? projects[0] : undefined} />
        }
      />
      <Stack flex={1} style={{ overflowY: 'auto' }} gap="md">
        <WorkOrderSection title="Action Needed" actionable defaultParams={defaultParams} />
        <WorkOrderSection title="Production" actionable={false} defaultParams={defaultParams} />
      </Stack>
    </Stack>
  );
};

export const MobileWorkOrdersPage = () => {
  const { customProperties: customWorkOrderProperties } = useCustomProperties('Work Order');
  const { states, statuses, loading } = useWorkOrderStatuses();

  if (loading || isNil(states) || isNil(customWorkOrderProperties)) {
    return <LoadingOverlay visible />;
  }

  return (
    <MobileWorkOrdersPageWrapped
      states={states}
      statuses={statuses}
      customWorkOrderProperties={customWorkOrderProperties}
    />
  );
};
